interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "danger" | "warning" | "info" | "default";
  isLoading?: boolean;
  hideConfirm?: boolean;
}

const variantStyles = {
  danger: {
    icon: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z",
    iconBg: "bg-red-500/20",
    iconColor: "text-red-400",
    confirmBg:
      "bg-red-500 hover:bg-red-600 shadow-lg shadow-red-500/25",
  },
  warning: {
    icon: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z",
    iconBg: "bg-yellow-500/20",
    iconColor: "text-yellow-400",
    confirmBg:
      "bg-yellow-500 hover:bg-yellow-600 shadow-lg shadow-yellow-500/25",
  },
  info: {
    icon: "M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
    iconBg: "bg-blue-500/20",
    iconColor: "text-blue-400",
    confirmBg:
      "bg-blue-500 hover:bg-blue-600 shadow-lg shadow-blue-500/25",
  },
  default: {
    icon: "M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
    iconBg: "bg-primary-500/20",
    iconColor: "text-primary-400",
    confirmBg:
      "bg-primary-500 hover:bg-primary-600 shadow-lg shadow-primary-500/25",
  },
};

/**
 * A themed confirmation dialog that replaces the native browser confirm().
 */
export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "danger",
  isLoading = false,
  hideConfirm = false,
}: ConfirmDialogProps) {
  if (!isOpen) return null;

  const styles = variantStyles[variant];

  const handleConfirm = () => {
    if (hideConfirm) {
      onClose();
    } else {
      onConfirm();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close dialog"
        onClick={onClose}
        disabled={isLoading}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
      />

      {/* Dialog */}
      <div className="relative z-10 w-full max-w-md bg-surface-900 border border-surface-700 rounded-2xl shadow-2xl overflow-hidden animate-fade-in">
        {/* Header with Icon */}
        <div className="px-6 py-5 flex items-start gap-4">
          <div
            className={`w-12 h-12 rounded-xl ${styles.iconBg} flex items-center justify-center flex-shrink-0`}
          >
            <svg
              className={`w-6 h-6 ${styles.iconColor}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={styles.icon}
              />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-surface-100">{title}</h2>
            <p className="mt-1 text-sm text-surface-400 leading-relaxed">
              {message}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 bg-surface-800/50 border-t border-surface-700/50 flex gap-3 justify-end">
          {!hideConfirm && (
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2.5 text-sm font-medium rounded-lg border border-surface-600 text-surface-300 hover:bg-surface-700/50 transition-colors disabled:opacity-50"
            >
              {cancelText}
            </button>
          )}
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isLoading}
            className={`px-4 py-2.5 text-sm font-medium rounded-lg text-white transition-all duration-200 disabled:opacity-50 flex items-center gap-2 ${styles.confirmBg}`}
          >
            {isLoading && (
              <svg
                className="w-4 h-4 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
